import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SettingsData } from './mockdata/SettingsData';
import styles from './CSS/AdminSettings.module.css';
import { useAdminSettings, useUpdateSettings } from '../../../hooks/admin/useAdminSettings';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('userInterface');
  const { settingsData, isLoading: isLoadingSettings } = useAdminSettings();
  const { updateSettings, isLoading: isUpdating } = useUpdateSettings();

  // State for tracking current settings and unsaved changes
  const [currentSettings, setCurrentSettings] = useState(() => {
    const initialSettings = {};
    Object.keys(SettingsData).forEach(category => {
      SettingsData[category].settings.forEach(setting => {
        initialSettings[setting.id] = setting.defaultValue;
      });
    });
    return initialSettings;
  });

  // State for tracking unsaved changes
  const [unsavedSettings, setUnsavedSettings] = useState({});

  // Update local state when settings data is loaded
  React.useEffect(() => {
    if (settingsData) {
      setCurrentSettings(settingsData);
      setUnsavedSettings({});
    }
  }, [settingsData]);

  const handleSettingChange = (settingId, value) => {
    setUnsavedSettings(prev => ({
      ...prev,
      [settingId]: value
    }));
  };

  const renderSettingInput = (setting) => {
    // Get the current value (prefer unsaved changes if they exist)
    const currentValue = unsavedSettings[setting.id] !== undefined 
      ? unsavedSettings[setting.id] 
      : currentSettings[setting.id];

    switch (setting.type) {
      case 'toggle':
        return (
          <label className={styles.toggleSwitch}>
            <input
              type="checkbox"
              checked={currentValue}
              onChange={(e) => handleSettingChange(setting.id, e.target.checked)}
              disabled={isUpdating}
            />
            <span className={styles.slider}></span>
          </label>
        );

      case 'select':
        return (
          <select
            value={currentValue}
            onChange={(e) => handleSettingChange(setting.id, e.target.value)}
            className={styles.select}
            disabled={isUpdating}
          >
            {setting.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multiSelect':
        return (
          <div className={styles.multiSelect}>
            {setting.options.map(option => (
              <label key={option} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={currentValue?.includes(option)}
                  onChange={(e) => {
                    const newValue = e.target.checked
                      ? [...(currentValue || []), option]
                      : (currentValue || []).filter(item => item !== option);
                    handleSettingChange(setting.id, newValue);
                  }}
                  disabled={isUpdating}
                />
                {option}
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const saveSettings = async () => {
    try {
      // Merge current settings with unsaved changes
      const settingsToSave = {
        ...currentSettings,
        ...unsavedSettings
      };
      
      await updateSettings(settingsToSave);
      
      // Update current settings and clear unsaved changes
      setCurrentSettings(settingsToSave);
      setUnsavedSettings({});
      
      // Show success toast
      toast.success('Settings saved successfully!');
    } catch (error) {
      // Show error toast
      toast.error('Failed to save settings. Please try again.');
    }
  };

  const hasUnsavedChanges = Object.keys(unsavedSettings).length > 0;

  if (isLoadingSettings) {
    return <div className={styles.loading}>Loading settings...</div>;
  }

  return (
    <div className={styles.adminSettingsContainer}>
      <ToastContainer />
      <h1 className={styles.mainTitle}>Admin Settings</h1>
      
      <div className={styles.settingsWrapper}>
        <div className={styles.sidebar}>
          {Object.keys(SettingsData).map(category => (
            <button
              key={category}
              className={`${styles.tabButton} ${activeTab === category ? styles.activeTab : ''}`}
              onClick={() => setActiveTab(category)}
              disabled={isUpdating}
            >
              {SettingsData[category].label}
            </button>
          ))}
        </div>

        <div className={styles.settingsContent}>
          <h2 className={styles.categoryTitle}>{SettingsData[activeTab].label}</h2>
          
          <div className={styles.settingsList}>
            {SettingsData[activeTab].settings.map(setting => (
              <div key={setting.id} className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <h3 className={styles.settingTitle}>{setting.label}</h3>
                  <p className={styles.settingDescription}>{setting.description}</p>
                </div>
                <div className={styles.settingControl}>
                  {renderSettingInput(setting)}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.actionButtons}>
            <button 
              className={styles.saveButton} 
              onClick={saveSettings}
              disabled={isUpdating || !hasUnsavedChanges}
            >
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;